/**
 * Shared constants + table + view-toggle components used by both My Feedback
 * and Admin Feedback pages.
 */
import { useState } from 'react';
import {
  Box, Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { ViewList, ViewModule } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import type { FeedbackTicket } from '../../types/feedback';

// Muted earth-tone palette chosen to harmonize with MoftakiLab's warm olive-
// on-cream aesthetic. Each status is mid-tone, similarly desaturated, so they
// read as a family instead of clashing primary-Material swatches.
export const STATUS_COLORS: Record<string, string> = {
  New: '#4A6D8C',                 // muted slate blue
  'Ready for Testing': '#6B4E7D', // dusty aubergine — fix is up, awaiting submitter
  'Needs Rework': '#A05432',      // burnt sienna — signals "rejected, redo"
  'Waiting on Submitter': '#A8842C', // ochre amber — attention: we need YOUR answer to continue
  Fixed: '#5A7A4F',               // sage (sibling to primary olive)
  'Confirmed Fixed': '#3E6B36',   // deeper olive — locked, user-confirmed
  Deferred: '#8A5A4A',            // muted terracotta
};

interface FeedbackTicketTableProps {
  tickets: FeedbackTicket[];
  onRowClick: (ticket: FeedbackTicket) => void;
  showSubmitter?: boolean;
  showAdminPriority?: boolean;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onToggleSelect?: (key: string) => void;
  onToggleSelectAll?: () => void;
}

export const FeedbackTicketTable = ({
  tickets, onRowClick, showSubmitter, showAdminPriority,
  selectable, selectedKeys, onToggleSelect, onToggleSelectAll,
}: FeedbackTicketTableProps) => {
  if (tickets.length === 0) {
    return <Typography color="text.secondary">No feedback tickets yet.</Typography>;
  }

  const headers = ['Ticket', 'Type', 'Title'];
  if (showSubmitter) headers.push('Submitted By');
  headers.push('Status', 'Sev / Priority');
  if (showAdminPriority) headers.push('Admin Pri');
  headers.push('Created');

  const allSelected = selectable && selectedKeys ? tickets.every((t) => selectedKeys.has(t.FeedbackTicketKey)) : false;
  const someSelected = selectable && selectedKeys ? tickets.some((t) => selectedKeys.has(t.FeedbackTicketKey)) && !allSelected : false;

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allSelected} indeterminate={someSelected}
                  onChange={onToggleSelectAll} />
              </TableCell>
            )}
            {headers.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.map((t) => {
            const statusColor = STATUS_COLORS[t.Status] || '#616161';
            const isSelected = selectable && selectedKeys ? selectedKeys.has(t.FeedbackTicketKey) : false;
            return (
              <TableRow key={t.FeedbackTicketKey} hover selected={isSelected} onClick={() => onRowClick(t)}
                sx={{ cursor: 'pointer', '& .MuiTableCell-body': { fontSize: '0.8rem', py: 1 } }}>
                {selectable && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox size="small" checked={isSelected}
                      onChange={() => onToggleSelect?.(t.FeedbackTicketKey)} />
                  </TableCell>
                )}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.TicketNumber}</TableCell>
                <TableCell>
                  <Chip label={t.Type === 'bug' ? 'Bug' : 'Feature'} size="small"
                    sx={{ fontSize: '0.7rem', height: 22, bgcolor: t.Type === 'bug' ? '#ffebee' : '#e8f5e9', color: t.Type === 'bug' ? '#d32f2f' : '#2e7d32' }} />
                </TableCell>
                <TableCell sx={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.RefinedTitle || t.Title}</TableCell>
                {showSubmitter && <TableCell>{t.SubmittedByName || t.SubmittedByEmail}</TableCell>}
                <TableCell>
                  <Chip label={t.Status} size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: alpha(statusColor, 0.12), color: statusColor }} />
                </TableCell>
                <TableCell>{t.Severity || t.Priority || '—'}</TableCell>
                {showAdminPriority && <TableCell>{t.AdminPriority || '—'}</TableCell>}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.sysCreateDate ? new Date(t.sysCreateDate).toLocaleDateString() : ''}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Simple label/value row for the detail sidebar. */
export const DetailRow = ({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.68rem', color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontSize: '0.72rem', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</Typography>
    </Box>
  );
};

export type FeedbackViewMode = 'list' | 'cards';

/** Hook that remembers the user's list/card choice per page. */
export function useFeedbackViewMode(storageKey: string): [FeedbackViewMode, (m: FeedbackViewMode) => void] {
  const read = (): FeedbackViewMode => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === 'cards' ? 'cards' : 'list';
    } catch { return 'list'; }
  };
  const [mode, setModeState] = useState<FeedbackViewMode>(read());
  const setMode = (m: FeedbackViewMode) => {
    setModeState(m);
    try { localStorage.setItem(storageKey, m); } catch { /* ignore */ }
  };
  return [mode, setMode];
}

interface ViewModeToggleProps {
  value: FeedbackViewMode;
  onChange: (v: FeedbackViewMode) => void;
}

export const ViewModeToggle = ({ value, onChange }: ViewModeToggleProps) => (
  <ToggleButtonGroup
    size="small" exclusive value={value}
    onChange={(_, v) => v && onChange(v)}
    sx={{
      '& .MuiToggleButton-root': {
        px: 1, py: 0.3, border: '1px solid',
        borderColor: 'divider',
        '&.Mui-selected': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          borderColor: 'primary.main',
        },
      },
    }}>
    <ToggleButton value="list">
      <Tooltip title="List view"><ViewList sx={{ fontSize: 18 }} /></Tooltip>
    </ToggleButton>
    <ToggleButton value="cards">
      <Tooltip title="Card view"><ViewModule sx={{ fontSize: 18 }} /></Tooltip>
    </ToggleButton>
  </ToggleButtonGroup>
);
